import { Check, type LucideIcon } from 'lucide-react';

export interface SegOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

interface Common<T extends string> {
  options: SegOption<T>[];
  size?: 'sm' | 'md';
  showLabels?: boolean;
  label: string;
  className?: string;
}

const track = 'flex rounded-md bg-surface2 p-0.5 gap-0.5';
const itemBase = 'flex flex-1 items-center justify-center gap-1.5 rounded-[7px] px-2 min-w-0';
const itemOn = 'bg-selected text-selected-ink font-bold';
const itemOff = 'text-dim hover:bg-white/5';

/** Single-select segmented control (radio group). docs/spec/06 §2. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  showLabels = true,
  label,
  className = '',
}: Common<T> & { value: T; onChange: (v: T) => void }) {
  const h = size === 'sm' ? 'h-8' : 'h-10';
  const focusMove = (dir: 1 | -1) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + dir + options.length) % options.length];
    onChange(next.value);
  };
  return (
    <div role="radiogroup" aria-label={label} className={`${track} ${className}`}>
      {options.map((o) => {
        const on = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={showLabels ? undefined : o.label}
            title={showLabels ? undefined : o.label}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                focusMove(1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                focusMove(-1);
              }
            }}
            className={`${itemBase} ${h} t-label ${on ? itemOn : itemOff}`}
          >
            {Icon ? <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2.2} aria-hidden /> : null}
            {showLabels ? <span className="truncate">{o.label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select: each segment toggles, at least one stays on, values kept in option order. */
export function MultiSegmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  showLabels = true,
  label,
  className = '',
}: Common<T> & { value: T[]; onChange: (v: T[]) => void }) {
  const h = size === 'sm' ? 'h-8' : 'h-10';
  return (
    <div role="group" aria-label={label} className={`${track} ${className}`}>
      {options.map((o) => {
        const on = value.includes(o.value);
        const Icon = on ? Check : o.icon;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => {
              const next = on ? value.filter((v) => v !== o.value) : [...value, o.value];
              if (next.length === 0) return;
              onChange(options.map((x) => x.value).filter((v) => next.includes(v)));
            }}
            className={`${itemBase} ${h} t-label ${on ? itemOn : itemOff}`}
          >
            {Icon ? <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2.2} aria-hidden /> : null}
            {showLabels ? <span className="truncate">{o.label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
